import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Flex, Spinner, Text, useColorMode, ThemeUIStyleObject } from 'theme-ui';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { CreateByUrlApi, UpdateByIdApi, GetProjectbyId } from '../../../store/api';
import { getStageBadgeStyle, priorityOptions } from '../../../utils/constent';
import FormSelect from '../../forms-component/globalSelect';
import { Inputform } from '../../forms-component/InputForm';
import {
  AddManualDeliveryModalProps,
  AddManualDeliveryFormValues,
  IManualDeliveryRecord,
  IUserRecord,
  IStageRecord,
  IProjectRecord,
  SelectOption,
  ITaskItem,
} from './types';



const getRefId = (val: unknown): string => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null) {
    return (val as { _id?: string })._id || '';
  }
  return '';
};

const DEFAULT_TRADES: ITaskItem[] = [
  { _id: 'Mechanical', name: 'Mechanical (Standard)', shortForm: 'M', color: '#10B981' },
  { _id: 'Electrical', name: 'Electrical (Standard)', shortForm: 'E', color: '#F59E0B' },
  { _id: 'Plumbing', name: 'Plumbing (Standard)', shortForm: 'P', color: '#3B82F6' },
  { _id: 'Structural', name: 'Structural Design', shortForm: 'S', color: '#8B5CF6' },
];

const errorTextStyle: ThemeUIStyleObject = { fontSize: '12px', mt: '4px', display: 'block' };

const defaultFormValues: AddManualDeliveryFormValues = {
  projectId: '',
  projectName: '',
  stageId: '',
  cadOperator: '',
  deliveryDate: new Date().toISOString().split('T')[0],
  manager: '',
  priority: 'Low',
  comments: '',
  frontAppLink: '',
  trades: [],
};

const validationSchema = Yup.object().shape({
  projectId: Yup.string().required('Project name is required'),
  stageId: Yup.string().required('Stage is required'),
  cadOperator: Yup.string().required('CAD Operator is required'),
  deliveryDate: Yup.string().required('Date is required'),
  manager: Yup.string().required('Project Manager is required'),
  priority: Yup.string().required('Priority is required'),
  trades: Yup.array().of(Yup.string()).min(1, 'At least one trade is required').required('At least one trade is required'),
  frontAppLink: Yup.string()
    .nullable()
    .notRequired()
    .test('is-url', 'Please enter a valid URL (must start with http:// or https://)', (value) => {
      if (!value || !value.trim()) return true;
      try {
        const url = new URL(value.trim());
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch {
        return false;
      }
    }),
});

const FieldError: React.FC<{ name: keyof AddManualDeliveryFormValues; formik: FormikProps<AddManualDeliveryFormValues>; style?: ThemeUIStyleObject }> = ({ name, formik, style }) => {
  const isTouched = Boolean(formik.touched[name]);
  const errorMsg = formik.errors[name];
  if (!isTouched || !errorMsg) return null;
  return <Text className="text-danger" sx={style}>{typeof errorMsg === 'string' ? errorMsg : 'Invalid field'}</Text>;
};

export const AddManualDeliveryModal: React.FC<AddManualDeliveryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  deliveryRecord,
  isReadOnly = false,
}) => {
  const [colorMode] = useColorMode();
  const isDark = colorMode === 'dark';
  const isEdit = Boolean(deliveryRecord);
  const manualRecord = (deliveryRecord as unknown) as IManualDeliveryRecord | null;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [initialRecordValues, setInitialRecordValues] = useState<AddManualDeliveryFormValues | null>(null);

  const masterDataState = useSelector((state: { masterData?: { dataForTable?: Record<string, unknown> } }) => (state.masterData?.dataForTable || {}) as Record<string, unknown>);
  const createprojectMasterData = useSelector((state: { project?: { createprojectMasterData?: { allTask?: { data?: Array<Record<string, unknown>> }; allProjectManagers?: IUserRecord[] } } }) => state.project?.createprojectMasterData);
  const masterDataObj = useMemo(() => (masterDataState?.data as Record<string, unknown> | undefined) || masterDataState, [masterDataState]);

  const projectsList = useMemo<IProjectRecord[]>(() => {
    const sources = [masterDataObj?.allProjects, masterDataObj?.allDeliveredProjects, masterDataObj?.allRTDProjects, masterDataObj?.allDraftProjects];
    const map = new Map<string, IProjectRecord>();
    sources.forEach((list) => {
      if (Array.isArray(list)) {
        list.forEach((p: IProjectRecord) => {
          if (p?._id && p.name) {
            const id = String(p._id);
            map.set(id, { ...p, ...(map.get(id) || {}) });
          }
        });
      }
    });
    return Array.from(map.values());
  }, [masterDataObj]);

  const projectOptions = useMemo<SelectOption[]>(() => projectsList.map((p) => ({ label: p.name, value: p._id })), [projectsList]);

  const projectManagers = useMemo<IUserRecord[]>(() => {
    const mDataPms = (Array.isArray(masterDataObj?.allProjectManagers) ? masterDataObj.allProjectManagers : []) as IUserRecord[];
    const cpData = createprojectMasterData as { allProjectManagers?: IUserRecord[] } | undefined;
    const cpPms = (cpData && Array.isArray(cpData.allProjectManagers) ? cpData.allProjectManagers : []) as IUserRecord[];

    const map = new Map<string, IUserRecord>();
    [...mDataPms, ...cpPms].forEach((u) => {
      if (u?._id) map.set(String(u._id), u);
    });
    return Array.from(map.values());
  }, [masterDataObj, createprojectMasterData]);

  const formik = useFormik<AddManualDeliveryFormValues>({
    initialValues: defaultFormValues,
    validationSchema,
    onSubmit: async (values) => {
      if (!selectedTrades?.length) {
        setIsSubmitting(false);
        formik.setFieldError('trades', 'At least one trade is required');
        formik.setFieldTouched('trades', true, false);
        return;
      }
      setIsSubmitting(true);
      const formattedDeliveryDate = values.deliveryDate ? values.deliveryDate.split('T')[0] : '';

      try {
        let payload: Record<string, unknown> = {};
        let manualItemId = '';

        if (manualRecord) {
          manualItemId = manualRecord._id || (deliveryRecord as { id?: string })?.id || '';

          if (initialRecordValues) {
            const isTradesChanged = JSON.stringify([...selectedTrades].sort()) !== JSON.stringify([...(initialRecordValues.trades || [])].sort());
            if (isTradesChanged) payload.tasksNameList = selectedTrades;
            if (values.projectId && values.projectId !== initialRecordValues.projectId) payload.projectId = values.projectId;
            if (values.stageId && values.stageId !== initialRecordValues.stageId) payload.stageId = values.stageId;
            if (values.cadOperator && values.cadOperator !== initialRecordValues.cadOperator) payload.cadOperator = values.cadOperator;
            if (values.manager && values.manager !== initialRecordValues.manager) payload.manager = values.manager;
            if (values.priority && values.priority !== initialRecordValues.priority) payload.priority = values.priority;
            if (values.deliveryDate && values.deliveryDate !== initialRecordValues.deliveryDate) payload.deliveryDate = formattedDeliveryDate;
            if (values.comments.trim() !== initialRecordValues.comments) payload.comments = values.comments.trim() ? [{ text: values.comments.trim() }] : [];
            if (values.frontAppLink.trim() !== initialRecordValues.frontAppLink) payload.frontAppLinks = values.frontAppLink.trim() ? [{ link: values.frontAppLink.trim() }] : [];
          } else {
            Object.assign(payload, {
              stageId: values.stageId || manualRecord.stageId?._id || '',
              deliveryDate: formattedDeliveryDate,
              ...(values.projectId && { projectId: values.projectId }),
              ...(values.manager && { manager: values.manager }),
              ...(values.priority && { priority: values.priority }),
              ...(values.cadOperator && { cadOperator: values.cadOperator }),
              ...(values.comments.trim() && { comments: [{ text: values.comments.trim() }] }),
              ...(values.frontAppLink.trim() && { frontAppLinks: [{ link: values.frontAppLink.trim() }] }),
              ...(selectedTrades.length && { tasksNameList: selectedTrades }),
            });
          }

          if (Object.keys(payload).length === 0) {
            setIsSubmitting(false);
            onClose();
            return;
          }
        } else {
          payload = {
            projectId: values.projectId,
            stageId: values.stageId,
            cadOperator: values.cadOperator,
            priority: values.priority || 'Low',
            deliveryDate: formattedDeliveryDate,
            tasksNameList: selectedTrades,
            ...(values.manager && { manager: values.manager }),
            ...(values.comments.trim() && { comments: [{ text: values.comments.trim() }] }),
            ...(values.frontAppLink.trim() && { frontAppLinks: [{ link: values.frontAppLink.trim() }] }),
          };
        }

        const apiCall = manualRecord
          ? UpdateByIdApi({ Query: `/deliveries/manual-delivery-items/${manualItemId}`, obj: payload })
          : CreateByUrlApi({ Query: '/deliveries/manual-delivery-items', obj: payload });

        const res = (await apiCall) as { data?: { status?: boolean; message?: string } };
        if (res?.data?.status !== false) {
          toast.success(res?.data?.message || `Manual delivery item ${manualRecord ? 'updated' : 'created'} successfully`);
          onClose();
        } else {
          toast.error(res?.data?.message || `Failed to ${manualRecord ? 'update' : 'add'} item`);
        }
      } catch (error: unknown) {
        const axiosErr = error as { response?: { data?: { message?: string } }; message?: string };
        toast.error(axiosErr?.response?.data?.message || axiosErr?.message || 'Something went wrong');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const userOptions = useMemo<SelectOption[]>(() => {
    const options: SelectOption[] = projectManagers.map((u) => ({
      label: `${u.FirstName || ''} ${u.LastName || ''}`.trim() || u.username || 'User',
      value: String(u._id),
    }));

    if (formik.values.manager && !options.some((opt) => opt.value === formik.values.manager)) {
      const selectedProj = projectsList.find((p) => String(p._id) === String(formik.values.projectId));
      const pmObj = selectedProj?.projectManager as { FirstName?: string; LastName?: string } | undefined;
      const pmLabel = pmObj ? `${pmObj.FirstName || ''} ${pmObj.LastName || ''}`.trim() : '';
      options.push({ label: pmLabel || 'Project Manager', value: formik.values.manager });
    }

    if (formik.values.cadOperator && !options.some((opt) => opt.value === formik.values.cadOperator)) {
      const cadObj = manualRecord?.cadOperator as { FirstName?: string; LastName?: string; _id?: string } | undefined;
      const cadLabel = cadObj && typeof cadObj === 'object'
        ? `${cadObj.FirstName || ''} ${cadObj.LastName || ''}`.trim()
        : (typeof manualRecord?.cadOperator === 'string' && !/^[0-9a-fA-F]{24}$/.test(manualRecord.cadOperator) ? manualRecord.cadOperator : '');
      options.push({ label: cadLabel || 'CAD Operator', value: formik.values.cadOperator });
    }

    return options;
  }, [projectManagers, formik.values.manager, formik.values.cadOperator, formik.values.projectId, projectsList, manualRecord]);

  const stagesList = useMemo<IStageRecord[]>(() => {
    const rawStages = (Array.isArray(masterDataObj?.allStages) ? masterDataObj.allStages : []) as IStageRecord[];
    const allowed = ['prelim', 'full', 'pc', 'rev'];
    return rawStages.filter((s) => allowed.includes(String(s.name || '').toLowerCase().trim()));
  }, [masterDataObj]);

  const stageOptions = useMemo<SelectOption[]>(() => stagesList.map((s) => ({ label: s.name, value: s._id })), [stagesList]);

  const availableTrades = useMemo<ITaskItem[]>(() => {
    const rawTasks = (createprojectMasterData?.allTask?.data || []) as Array<Record<string, unknown>>;
    const allowed = ['mechanical (standard)', 'electrical (standard)', 'plumbing (standard)', 'structural design'];

    if (Array.isArray(rawTasks) && rawTasks.length > 0) {
      const filtered = rawTasks.filter((t) => allowed.some((target) => String(t.name || '').toLowerCase().trim().includes(target)));
      if (filtered.length > 0) {
        return filtered.map((t) => ({
          _id: String(t._id || t.id || t.name || ''),
          name: String(t.name || ''),
          shortForm: String(t.shortForm || t.shortform || (t.name ? String(t.name).charAt(0) : '')),
          color: String(t.color || '#3B82F6'),
        }));
      }
    }

    return DEFAULT_TRADES;
  }, [createprojectMasterData]);

  const toggleTrade = (tradeVal: string) => {
    const nextTrades = selectedTrades.includes(tradeVal)
      ? selectedTrades.filter((item) => item !== tradeVal)
      : [...selectedTrades, tradeVal];
    setSelectedTrades(nextTrades);
    formik.setFieldValue('trades', nextTrades, true);
    if (nextTrades.length > 0) {
      formik.setFieldError('trades', undefined);
    } else {
      formik.setFieldError('trades', 'At least one trade is required');
    }
    formik.setFieldTouched('trades', true, false);
  };

  useEffect(() => {
    if (!isOpen) return;
    setIsSubmitting(false);

    if (manualRecord) {
      const rec = manualRecord;
      const projId = getRefId(rec.projectId);
      const projName = typeof rec.projectId === 'object' && rec.projectId !== null ? rec.projectId.name || '' : '';
      const stageIdVal = getRefId(rec.stageId);

      let cadOp = '';
      if (typeof rec.cadOperator === 'object' && rec.cadOperator !== null) {
        cadOp = rec.cadOperator._id || '';
      } else if (typeof rec.cadOperator === 'string' && rec.cadOperator) {
        const cadOpName = rec.cadOperator;
        if (/^[0-9a-fA-F]{24}$/.test(cadOpName)) {
          cadOp = cadOpName;
        } else {
          const lowerName = cadOpName.toLowerCase();
          const matchedUser = projectManagers.find(
            (u) =>
              `${u.FirstName || ''} ${u.LastName || ''}`.trim().toLowerCase() === lowerName ||
              u.username?.toLowerCase() === lowerName
          );
          cadOp = matchedUser?._id ? String(matchedUser._id) : cadOpName;
        }
      }

      let mgr = getRefId(rec.manager);
      if (!mgr && typeof rec.projectId === 'object' && rec.projectId !== null) {
        mgr = rec.projectId.projectManager?._id || '';
      }

      const delDate = rec.deliveryDate ? rec.deliveryDate.split('T')[0] : '';
      const cmt = (rec.comments || []).map((c) => c?.text || '').filter(Boolean).join('\n');
      const frontApp = (rec.frontAppLinks || []).map((l) => l?.link || '').filter(Boolean).join('\n');
      const initialTradeKeys = (rec.tasksNameList || [])
        .map((t: unknown) => typeof t === 'string' ? t : (t as { _id?: string; id?: string })?._id || (t as { id?: string })?.id || '')
        .filter(Boolean);

      setSelectedTrades(initialTradeKeys);
      const values: AddManualDeliveryFormValues = {
        projectId: projId,
        projectName: projName,
        stageId: stageIdVal,
        cadOperator: cadOp,
        deliveryDate: delDate,
        manager: mgr,
        priority: rec.priority || 'Low',
        comments: cmt,
        frontAppLink: frontApp,
        trades: initialTradeKeys,
      };

      setInitialRecordValues(values);
      formik.setValues(values);
    } else {
      setInitialRecordValues(null);
      formik.resetForm({ values: defaultFormValues });
      setSelectedTrades([]);
    }
  }, [isOpen, manualRecord, projectManagers]);

  const currentProjName = useMemo(() => {
    if (formik.values.projectName) return formik.values.projectName;
    return manualRecord?.projectId?.name || '';
  }, [formik.values.projectName, manualRecord]);

  const currentStageName = useMemo(() => {
    if (!formik.values.stageId) return '';
    const stageObj = stagesList.find((s) => String(s._id) === String(formik.values.stageId));
    return stageObj ? String(stageObj.name || '') : '';
  }, [formik.values.stageId, stagesList]);

  const handleProjectChange = (projId: string) => {
    formik.setFieldValue('projectId', projId);
    if (!deliveryRecord) {
      formik.setFieldValue('stageId', '');
    }

    const p = projectsList.find((item) => String(item._id) === String(projId));
    formik.setFieldValue('projectName', p?.name || '');

    const pm = getRefId(p?.projectManager);

    if (pm) {
      formik.setFieldValue('manager', pm);
    } else if (projId) {
      GetProjectbyId(projId)
        .then((res: { data?: { data?: { projectManager?: { _id?: string } | string } } }) => {
          formik.setFieldValue('manager', getRefId(res?.data?.data?.projectManager));
        })
        .catch(() => {
          formik.setFieldValue('manager', '');
        });
    } else {
      formik.setFieldValue('manager', '');
    }
  };

  const handleStageChange = (stgId: string) => {
    formik.setFieldValue('stageId', stgId);
    const stageObj = stagesList.find((s) => String(s._id) === String(stgId)) as Record<string, unknown> | undefined;
    const stageDueDate = stageObj?.dueDate || stageObj?.clientDueDate || stageObj?.promise;
    const formattedDate = stageDueDate ? String(stageDueDate).split('T')[0] : '';

    if (formattedDate) {
      formik.setFieldValue('deliveryDate', formattedDate);
    } else if (!isEdit) {
      formik.setFieldValue('deliveryDate', '');
    }
  };

  const modalBg = isDark ? '#0F172A' : '#FFFFFF';
  const borderColor = isDark ? '#334155' : '#E2E8F0';
  const textColor = isDark ? '#F8FAFC' : '#0F172A';
  const subTextColor = isDark ? '#94A3B8' : '#64748B';
  const selectLabelColor = isDark ? '#F8FAFC' : '#0A0A0A';
  const labelStyle = useMemo(() => ({ color: selectLabelColor, lineHeight: '1.4', mb: '4px' }), [selectLabelColor]);
  const helperTextStyle = useMemo(() => ({ fontSize: '11px', color: subTextColor, mt: '6px' }), [subTextColor]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg" contentClassName="bg-transparent border-0 shadow-none" style={{ maxWidth: '750px' }}>
      <Box sx={{ backgroundColor: modalBg, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)', border: `1px solid ${borderColor}` }}>
        {/* Header */}
        <Flex sx={{ justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px 24px 16px', borderBottom: `1px solid ${borderColor}` }}>
          <Flex sx={{ gap: '14px', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Text sx={{ fontSize: '18px', fontWeight: 700, color: textColor, lineHeight: 1.3, display: 'block' }}>
                {currentProjName ? (
                  <>
                    <span>{currentProjName}</span>
                    {currentStageName && <span style={{ color: subTextColor, margin: '0 6px' }}>&gt;</span>}
                  </>
                ) : (
                  'Add Manual Delivery Item'
                )}
                {currentStageName && <span style={{ color: getStageBadgeStyle(currentStageName).color }}>{currentStageName}</span>}
              </Text>
            </Box>
          </Flex>

          <Box
            onClick={onClose}
            sx={{
              cursor: 'pointer',
              color: subTextColor,
              padding: '6px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ml: '12px',
              '&:hover': { backgroundColor: isDark ? '#334155' : '#F1F5F9', color: textColor },
            }}
          >
            <FiX size={20} />
          </Box>
        </Flex>

        {/* Form Body */}
        <form onSubmit={formik.handleSubmit}>
          <ModalBody sx={{ padding: '24px' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
              <Box sx={{ cursor: isReadOnly ? 'not-allowed' : 'default' }}>
                <FormSelect
                  sx={labelStyle}
                  label="Project Name"
                  asterisk
                  placeholder="Select project name"
                  DefultSelect="Select project name"
                  value={formik.values.projectId || null}
                  options={projectOptions}
                  isMulti={false}
                  disabled={isReadOnly}
                  onChange={(selected: SelectOption | null) => handleProjectChange(selected?.value || '')}
                  onBlur={() => formik.setFieldTouched('projectId', true)}
                />
                <FieldError name="projectId" formik={formik} style={errorTextStyle} />
              </Box>

              <Box sx={{ cursor: isReadOnly ? 'not-allowed' : 'default' }}>
                <FormSelect
                  sx={labelStyle}
                  label="Stage"
                  asterisk
                  placeholder="Select stage"
                  DefultSelect="Select stage"
                  value={formik.values.stageId || null}
                  options={stageOptions}
                  isMulti={false}
                  disabled={isReadOnly}
                  onChange={(selected: SelectOption | null) => handleStageChange(selected?.value || '')}
                  onBlur={() => formik.setFieldTouched('stageId', true)}
                />
                <FieldError name="stageId" formik={formik} style={errorTextStyle} />
              </Box>

              <Box>
                <FormSelect
                  sx={labelStyle}
                  label="CAD Operator"
                  asterisk
                  placeholder="Select CAD operator"
                  DefultSelect="Select CAD operator"
                  value={formik.values.cadOperator || null}
                  options={userOptions}
                  isMulti={false}
                  onChange={(selected: SelectOption | null) => formik.setFieldValue('cadOperator', selected?.value || '')}
                  onBlur={() => formik.setFieldTouched('cadOperator', true)}
                />
                <FieldError name="cadOperator" formik={formik} style={errorTextStyle} />
              </Box>

              <Box>
                <Inputform
                  sx={labelStyle}
                  label="Date"
                  asterisk
                  type="date"
                  Name="deliveryDate"
                  Placeholder="Select date"
                  values={formik.values.deliveryDate}
                  OnChange={(e) => formik.setFieldValue('deliveryDate', e.target.value)}
                  onBlur={() => formik.setFieldTouched('deliveryDate', true)}
                />
                <FieldError name="deliveryDate" formik={formik} style={errorTextStyle} />
              </Box>

              <Box sx={{ cursor: Boolean(formik.values.projectId) || isReadOnly ? 'not-allowed' : 'default' }}>
                <FormSelect
                  sx={labelStyle}
                  label="Project Manager"
                  asterisk
                  placeholder="Project manager"
                  DefultSelect="Project manager"
                  value={formik.values.manager || null}
                  options={userOptions}
                  isMulti={false}
                  disabled={Boolean(formik.values.projectId) || isReadOnly}
                  onChange={(selected: SelectOption | null) => formik.setFieldValue('manager', selected?.value || '')}
                  onBlur={() => formik.setFieldTouched('manager', true)}
                />
                <FieldError name="manager" formik={formik} style={errorTextStyle} />
              </Box>

              <Box>
                <Text sx={{ fontSize: '13px', fontWeight: 600, color: selectLabelColor, mb: '4px', lineHeight: '1.4', display: 'block' }}>
                  Trades <Text as="span" sx={{ color: '#EF4444' }}>*</Text>
                </Text>
                <Flex sx={{ gap: '12px', mt: '8px', alignItems: 'center' }}>
                  {availableTrades.map((task, idx) => {
                    const tradeKey = task._id || task.name || '';
                    const isSelected = selectedTrades.includes(tradeKey);
                    const mainColor = task.color || '#3B82F6';

                    return (
                      <Box
                        key={task._id ? `${task._id}-${idx}` : idx}
                        onClick={() => toggleTrade(tradeKey)}
                        variant="badges.tradeBadge"
                        sx={{
                          backgroundColor: isSelected ? mainColor : isDark ? '#1E293B' : '#FFFFFF',
                          color: isSelected ? '#FFFFFF' : mainColor,
                          border: `2px solid ${mainColor}`,
                          boxShadow: isSelected ? `0 4px 10px ${mainColor}40` : 'none',
                          '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 4px 8px ${mainColor}30` },
                        }}
                        title={task.name}
                      >
                        {task.shortForm || task.name?.charAt(0) || ''}
                      </Box>
                    );
                  })}
                </Flex>
                <FieldError name="trades" formik={formik} style={errorTextStyle} />
                <Text sx={helperTextStyle}>Select all applicable trades.</Text>
              </Box>

              <Box>
                <FormSelect
                  sx={labelStyle}
                  label="Priority"
                  asterisk
                  placeholder="Select priority"
                  value={formik.values.priority}
                  options={priorityOptions}
                  isMulti={false}
                  onChange={(selected: SelectOption | null) => formik.setFieldValue('priority', selected?.value || 'Low')}
                  onBlur={() => formik.setFieldTouched('priority', true)}
                />
                <FieldError name="priority" formik={formik} style={errorTextStyle} />
              </Box>

              <Box>
                <Inputform
                  sx={labelStyle}
                  label="Link (Front App link)"
                  type="text"
                  Name="frontAppLink"
                  Placeholder="Paste Front App link"
                  values={formik.values.frontAppLink}
                  OnChange={(e) => formik.setFieldValue('frontAppLink', e.target.value)}
                  onBlur={() => formik.setFieldTouched('frontAppLink', true)}
                />
                <FieldError name="frontAppLink" formik={formik} style={errorTextStyle} />
                <Text sx={helperTextStyle}>Add link to Front App item (optional).</Text>
              </Box>

              <Box sx={{ gridColumn: '1 / -1' }}>
                <Inputform
                  sx={labelStyle}
                  label="Comments"
                  type="textarea"
                  Name="comments"
                  Placeholder="Add any additional details..."
                  values={formik.values.comments}
                  OnChange={(e) => formik.setFieldValue('comments', e.target.value)}
                />
              </Box>
            </Box>
          </ModalBody>

          <ModalFooter style={{ padding: '16px 24px', borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', backgroundColor: 'transparent' }}>
            <Button type="button" variant="CancelButton" onClick={onClose} sx={{ mr: 2, cursor: 'pointer' }}>
              Cancel
            </Button>
            {!isReadOnly && (
              <Button
                type="submit"
                variant="SaveButton"
                disabled={isSubmitting}
                sx={{
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minWidth: isEdit ? '160px' : '140px',
                  minHeight: '38px',
                }}
              >
                {isSubmitting ? (
                  <Flex sx={{ alignItems: 'center', gap: '8px' }}>
                    <Spinner size={16} color="#FFFFFF" />
                    <span>{isEdit ? 'Updating...' : 'Adding...'}</span>
                  </Flex>
                ) : (
                  isEdit ? 'Update Manual Item' : 'Add Manual Item'
                )}
              </Button>
            )}
          </ModalFooter>
        </form>
      </Box>
    </Modal>
  );
};

export default AddManualDeliveryModal;
